import * as XLSX from 'xlsx';

/** Adhérent SAR ayant réglé un droit d'entrée (adhésion estNouveau + paiement effectué). */
export const hasPaidSarDroitEntree = (user) => {
  const sarAdhesions = (user.adhesions || []).filter((a) => a.organisme === 'SAR');
  return sarAdhesions.some(
    (a) =>
      a.estNouveau === true
      && (a.paiementStatus === 'paye' || a.status === 'actif' || a.status === 'expiree')
  );
};

const formatSarDroitEntreePaye = (user, includeColumn) => {
  if (!includeColumn) return undefined;
  return hasPaidSarDroitEntree(user) ? 'Oui' : 'Non';
};

// Fonction utilitaire pour exporter les données d'adhésions en Excel
export const exportAdhesionsToExcel = (
  filteredUsers,
  getStatusLabel,
  getServiceTypeLabel,
  getServiceStatusLabel,
  { includeSarDroitEntreeColumn = false } = {}
) => {
  // Préparer les données pour l'export
  const exportData = [];
  
  filteredUsers.forEach(user => {
    if (user.adhesions && user.adhesions.length > 0) {
      user.adhesions.forEach(adhesion => {
        const droitEntreePaye = formatSarDroitEntreePaye(user, includeSarDroitEntreeColumn);
        // Ligne principale de l'adhésion
        const baseRow = {
          'Type': 'Adhésion',
          'Organisme': adhesion.organisme || '',
          'Service': '',
          'Année': adhesion.annee || '',
          'Statut': getStatusLabel(adhesion.status) || '',
          ...(includeSarDroitEntreeColumn
            ? { "Droit d'entrée payé": droitEntreePaye }
            : {}),
          'Type Personne': user.typePersonne === 'personne_physique' ? 'Personne physique' : 'Personne morale',
          'Nom': user.nom || '',
          'Prénom': user.prenom || '',
          'Raison Sociale': user.raisonSociale || '',
          'Email': user.email || '',
          'Téléphone': user.telephone || '',
          'Téléphone mobile': user.telephoneMobile || '',
          'Nombre Ruches': adhesion.nombreRuches || '',
          'SIRET': adhesion.siret || '',
          'NAPI': adhesion.napi || '',
          'AMEXA': adhesion.amexa || '',
          'Date Création Adhésion': adhesion.createdAt ? new Date(adhesion.createdAt).toLocaleDateString('fr-FR') : '',
          'Date Création Utilisateur': user.createdAt ? new Date(user.createdAt).toLocaleDateString('fr-FR') : '',   
        };
        exportData.push(baseRow);

        // Ajouter les services si ils existent
        if (adhesion.services && adhesion.services.length > 0) {
          adhesion.services.forEach(service => {
            const serviceRow = {
              'Type': 'Service',
              'Organisme': adhesion.organisme || '',
              'Service': getServiceTypeLabel(service.typeService) || '',
              'Année': adhesion.annee || '',
              'Statut': getServiceStatusLabel(service.status) || '',
              ...(includeSarDroitEntreeColumn
                ? { "Droit d'entrée payé": droitEntreePaye }
                : {}),
              'Type Personne': user.typePersonne === 'personne_physique' ? 'Personne physique' : 'Personne morale',
              'Nom': user.nom || '',
              'Prénom': user.prenom || '',
              'Raison Sociale': user.raisonSociale || '',
              'Email': user.email || '',
              'Téléphone': user.telephone || '',
              'Téléphone mobile': user.telephoneMobile || '',
              'Nombre Ruches': adhesion.nombreRuches || '',
              'SIRET': adhesion.siret || '',
              'NAPI': adhesion.napi || '',
              'AMEXA': adhesion.amexa || '',
              'Date Création Adhésion': service.createdAt ? new Date(service.createdAt).toLocaleDateString('fr-FR') : '',
              'Date Création Utilisateur': user.createdAt ? new Date(user.createdAt).toLocaleDateString('fr-FR') : '',
            };
            exportData.push(serviceRow);
          });
        }
      });
    } else {
      // Utilisateur sans adhésion
      const userRow = {
        'Type': 'Utilisateur sans adhésion',
        'Organisme': '',
        'Service': '',
        'Année': '',
        'Statut': '',
        ...(includeSarDroitEntreeColumn
          ? { "Droit d'entrée payé": formatSarDroitEntreePaye(user, true) }
          : {}),
        'Type Personne': user.typePersonne === 'personne_physique' ? 'Personne physique' : 'Personne morale',
        'Nom': user.nom || '',
        'Prénom': user.prenom || '',
        'Raison Sociale': user.raisonSociale || '',
        'Email': user.email || '',
        'Téléphone': user.telephone || '',
        'Téléphone mobile': user.telephoneMobile || '',
        'Nombre Ruches': '',
        'SIRET': '',
        'NAPI': '',
        'AMEXA': '',
        'Date Création Adhésion': '',
        'Date Création Utilisateur': user.createdAt ? new Date(user.createdAt).toLocaleDateString('fr-FR') : '',
      };
      exportData.push(userRow);
    }
  });

  // Créer le workbook et la worksheet
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(exportData);

  // Ajuster la largeur des colonnes
  const colWidths = [
    { wch: 12 }, // Type
    { wch: 10 }, // Organisme
    { wch: 20 }, // Service
    { wch: 8 },  // Année
    { wch: 15 }, // Statut
    ...(includeSarDroitEntreeColumn ? [{ wch: 18 }] : []), // Droit d'entrée payé
    { wch: 18 }, // Type Personne
    { wch: 15 }, // Nom
    { wch: 15 }, // Prénom
    { wch: 25 }, // Raison Sociale
    { wch: 25 }, // Email
    { wch: 16 }, // Téléphone
    { wch: 16 }, // Téléphone mobile
    { wch: 12 }, // Nombre Ruches
    { wch: 15 }, // SIRET
    { wch: 15 }, // NAPI
    { wch: 15 }, // AMEXA
    { wch: 15 }, // Date Création Adhésion
    { wch: 15 }  // Date Création Utilisateur
  ];
  ws['!cols'] = colWidths;

  // Ajouter la worksheet au workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Adhésions');

  // Générer le nom du fichier avec la date
  const today = new Date().toISOString().split('T')[0];
  const filename = `adhesions-export-${today}.xlsx`;

  // Télécharger le fichier
  XLSX.writeFile(wb, filename);
};

const PRESENCE_STATUT_LABELS = {
  present: 'Présent',
  absent: 'Absent',
  represente: 'Représenté',
  excuse: 'Excusé',
};

export const formatNomPrenomDisplay = (nom, prenom) => {
  const nomRaw = (nom || '').trim();
  const prenomRaw = (prenom || '').trim();

  if (nomRaw && prenomRaw) {
    const nomFmt = nomRaw.toUpperCase();
    const prenomFmt = prenomRaw.charAt(0).toUpperCase() + prenomRaw.slice(1).toLowerCase();
    return `${nomFmt} ${prenomFmt}`;
  }

  if (nomRaw) {
    const parts = nomRaw.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      const family = parts[0].toUpperCase();
      const given = parts.slice(1).join(' ');
      const givenFmt = given.charAt(0).toUpperCase() + given.slice(1).toLowerCase();
      return `${family} ${givenFmt}`;
    }
    return nomRaw.toUpperCase();
  }

  if (prenomRaw) {
    return prenomRaw.charAt(0).toUpperCase() + prenomRaw.slice(1).toLowerCase();
  }

  return '';
};

export const formatPersonName = (person) => {
  if (!person) return '';
  return formatNomPrenomDisplay(person.nom, person.prenom) || person.email || '';
};

const BUREAU_FONCTION_LABELS = {
  president: 'Président(e)',
  vice_president: 'Vice-président(e)',
  vice_president_2: 'Vice-président(e)',
  secretaire: 'Secrétaire',
  secretaire_adjoint: 'Secrétaire adjoint(e)',
  tresorier: 'Trésorier(ère)',
  tresorier_adjoint: 'Trésorier(ère) adjoint(e)',
};

export const getPresenceFonctionLabel = (presence) => {
  if (presence?.fonctionLabel) return presence.fonctionLabel;
  const mapped = BUREAU_FONCTION_LABELS[presence?.fonction];
  if (mapped) return mapped;
  if (presence?.fonction) return presence.fonction;
  return '—';
};

const SIMPLE_PRESENCE_FONCTIONS = new Set(['Adhérent', 'Membre']);

/** Rang de tri : membres simples d'abord, puis autres fonctions, puis alpha sur le nom */
const getPresenceFonctionSortRank = (presence) => {
  const label = getPresenceFonctionLabel(presence);
  if (SIMPLE_PRESENCE_FONCTIONS.has(label)) return 0;

  const rankByLabel = {
    'Membre coopté': 10,
    'Membre de droit': 11,
    'Administrateur stagiaire': 12,
    Administrateur: 20,
    'Représentant coopératives': 30,
    'Représentant syndicat': 31,
    'Second collège': 32,
    'Vice-président 2': 40,
    'Vice-président(e)': 41,
    'Vice-président': 42,
    'Secrétaire adjoint(e)': 50,
    'Secrétaire-adjoint': 51,
    Secrétaire: 52,
    'Trésorier(ère) adjoint(e)': 60,
    'Trésorier-adjoint': 61,
    'Trésorier(ère)': 62,
    Trésorier: 63,
    'Président(e)': 70,
    Président: 71,
  };

  if (rankByLabel[label] !== undefined) return rankByLabel[label];

  // Libellés personnalisés (ex. second collège ADAR)
  const lower = label.toLowerCase();
  if (lower.includes('vice-président')) return 41;
  if (lower.includes('secrétaire') && lower.includes('adjoint')) return 50;
  if (lower.includes('secrétaire')) return 52;
  if (lower.includes('trésorier') && lower.includes('adjoint')) return 60;
  if (lower.includes('trésorier')) return 62;
  if (lower.includes('président')) return 70;
  if (lower.includes('représentant')) return 30;

  return 200;
};

export const sortPresencesByFonctionAndName = (presences) =>
  [...presences].sort((a, b) => {
    const rankDiff = getPresenceFonctionSortRank(a) - getPresenceFonctionSortRank(b);
    if (rankDiff !== 0) return rankDiff;
    const nameA = formatNomPrenomDisplay(a.nom, a.prenom);
    const nameB = formatNomPrenomDisplay(b.nom, b.prenom);
    return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
  });

export const formatRepresenteStatut = (presence) => {
  const mandataire = formatPersonName(presence.representePar);
  return mandataire ? `Représenté par ${mandataire}` : 'Représenté';
};

export const formatPouvoirsRecusLabel = (presence) => {
  const count = presence.pouvoirsRecus || 0;
  if (count === 0) return '0';
  const names = (presence.pouvoirsMandants || [])
    .map(formatPersonName)
    .filter(Boolean)
    .join(', ');
  return names ? `${count} (${names})` : String(count);
};

const getPresenceStatutLabel = (presence) => {
  if (presence.statut === 'represente') {
    return formatRepresenteStatut(presence);
  }
  return PRESENCE_STATUT_LABELS[presence.statut] || presence.statut || '';
};

export const exportPresencesToExcel = ({
  presences,
  reunion,
  isAG,
}) => {
  const exportData = sortPresencesByFonctionAndName(presences).map((p) => {
    const base = {
      Membre: formatNomPrenomDisplay(p.nom, p.prenom),
      Email: p.email || '',
    };
    if (isAG) {
      base['Années adhésion'] = p.anneesAdhesion?.length ? p.anneesAdhesion.join(', ') : '';
      base['Actif au jour de l\'AG'] = p.actifAuJourAG ? 'Oui' : 'Non';
    }
    return {
      ...base,
      Fonction: getPresenceFonctionLabel(p),
      Pouvoirs: formatPouvoirsRecusLabel(p),
      Statut: getPresenceStatutLabel(p),
    };
  });

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(exportData);

  const colWidths = isAG
    ? [
        { wch: 28 }, { wch: 28 }, { wch: 22 },
        { wch: 16 }, { wch: 14 }, { wch: 22 }, { wch: 48 }, { wch: 36 },
      ]
    : [
        { wch: 28 }, { wch: 28 }, { wch: 22 },
        { wch: 48 }, { wch: 36 },
      ];
  ws['!cols'] = colWidths;

  XLSX.utils.book_append_sheet(wb, ws, 'Présences');

  const reunionDate = reunion.date
    ? new Date(reunion.date).toLocaleDateString('fr-FR').replace(/\//g, '-')
    : '';
  const typeShort =
    reunion.type === 'assemblee_generale_ordinaire' ? 'AGO'
    : reunion.type === 'assemblee_generale_extraordinaire' ? 'AGE'
    : reunion.type === 'conseil_syndical' ? (reunion.organisme === 'SAR' ? 'CS' : 'CA')
    : reunion.type === 'bureau' ? 'Bureau'
    : 'reunion';
  const filename = `${reunion.organisme || 'ORG'}_presences_${typeShort}_${reunionDate}.xlsx`;

  XLSX.writeFile(wb, filename);
};
